import {
  Box,
  IconButton,
  List,
  Drawer as MuiDrawer,
  Stack,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useLocation } from "react-router-dom";
import LazyIcon from "../../components/LazyIcon";
import { AdminOnly } from "../../components/PermissionGuard";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useFeatureFlags } from "../../containers/FeatureFlagsContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { AirplaneTailsIcon } from "../Icons/AirplaneTailsIcon";
import { HangarIcon } from "../Icons/HangarIcon";
import { RampIcon } from "../Icons/RampIcon";
import { FBOMenu } from "./FBOMenu";
// import logo from "../../../airplx-logo-transparent-square-no-text.png";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const DRAWER_WIDTH = 73;
export const SIDE_DRAWER_WIDTH = 240;

const openedMixin = (theme) => ({
  width: SIDE_DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflow: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

export const DrawerHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: open ? "flex-end" : "flex-center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    // ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
  }),
  ...(!open && {
    // ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
  }),
}));

export const SideIconButton = ({
  active = false,
  onClickRoute,
  label,
  icon,
}) => {
  const [open, setOpen] = React.useState<boolean>(false);
  return (
    <Stack sx={{ m: 1.5 }}>
      <Tooltip title={label} placement="right">
        <IconButton
          size="large"
          sx={{
            color: active ? "primary.contrastText" : "primary.main",
            backgroundColor: active ? "primary.main" : "inherit",
            "&:hover": {
              color: "primary.main",
              backgroundColor: "action.hover",
            },
          }}
          onClick={onClickRoute}
        >
          {icon}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

interface SideDrawerProps {
  open: boolean;
  setOpen: (x: boolean) => void;
  onClickRoute: (pathname: string) => void;
  showAdmin: boolean;
}

export const SideDrawer: React.FC<SideDrawerProps> = ({
  open,
  setOpen,
  showAdmin = true,
  onClickRoute,
}) => {
  const { airplxIdentity, logout } = useIdentity();
  const location = useLocation();
  const isDev = useFeatureFlags().isDev;
  const { activeFBO } = useActiveFBOs();

  // Helper function to check if a route is active
  const isRouteActive = (routePath: string): boolean => {
    return location.pathname.startsWith(routePath);
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        zIndex: 4000 + 1249,
        width: DRAWER_WIDTH,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <img
        data-testid="sidedrawer_logo"
        src={logo}
        alt="AirPLX Logo"
        style={{
          marginLeft: "auto",
          marginRight: "auto",
          width: 25,
          marginTop: 10,
        }}
      />
      <Stack
        direction="column"
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <List sx={{ mt: 0, pt: 0 }}>
          {airplxIdentity.isadmin && activeFBO?.settings?.enableOpsCenter && (
            <SideIconButton
              active={location.pathname === "/ops-center"}
              label="Ops Center"
              icon={<LazyIcon name="SupportAgentIcon" />}
              onClickRoute={() => onClickRoute("/ops-center")}
            />
          )}
          <SideIconButton
            active={isRouteActive("/hangars")}
            label="Hangars"
            icon={<HangarIcon />}
            onClickRoute={() => onClickRoute("/hangars")}
          />
          <SideIconButton
            active={isRouteActive("/ramps")}
            label="Ramps"
            icon={<RampIcon />}
            onClickRoute={() => onClickRoute("/ramps")}
          />
          <SideIconButton
            active={location.pathname === "/tenants"}
            label="Aircraft"
            icon={<AirplaneTailsIcon />}
            onClickRoute={() => onClickRoute("/tenants")}
          />
          {activeFBO?.settings?.enableScheduling && (
            <SideIconButton
              active={location.pathname === "/schedule"}
              label="Trip Schedule"
              icon={<LazyIcon name="CalendarMonthIcon" />}
              onClickRoute={() => onClickRoute("/schedule")}
            />
          )}
          <SideIconButton
            active={location.pathname === "/fee-calculator"}
            label="Fee Calculator"
            icon={<LazyIcon name="MonetizationOnIcon" />}
            onClickRoute={() => onClickRoute("/fee-calculator")}
          />

          {airplxIdentity.isadmin && 1 === 2 && (
            <SideIconButton
              active={location.pathname === "/hangar-builder-optimizer"}
              label="Hangar Optimization Calculator"
              icon={<LazyIcon name="CalculateIcon" />}
              onClickRoute={() => onClickRoute("/hangar-builder-optimizer")}
            />
          )}
          <SideIconButton
            active={false}
            label="Help"
            icon={<LazyIcon name="HelpIcon" />}
            onClickRoute={() => {
              window
                .open(
                  "https://help.airplx.com/docs/Hangars%20and%20Ramps/Current%20Hangar",
                  "_blank"
                )
                .focus();
            }}
          />
        </List>
        <Box>
          <AdminOnly>
            <SideIconButton
              active={location.pathname === "/super-admin"}
              label="Super Admin"
              icon={<LazyIcon name="SupportIcon" />}
              onClickRoute={() => onClickRoute("/super-admin")}
            />
          </AdminOnly>
          <SideIconButton
            active={isRouteActive("/settings")}
            label="Settings"
            icon={<LazyIcon name="SettingsIcon" />}
            onClickRoute={() => onClickRoute("/settings")}
          />
          <FBOMenu />
          <SideIconButton
            label="Logout"
            icon={<LazyIcon name="LogoutIcon" />}
            onClickRoute={() => {
              logout();
              onClickRoute("/login");
            }}
          />
        </Box>
      </Stack>
    </Drawer>
  );
};
